import { useEffect, useMemo, useRef, useState, type ElementType, type MutableRefObject, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import { useQuery } from '@tanstack/react-query';
import {
  X, Plus, Trash2, Loader2, CheckCircle2, ClipboardList, Link2, EyeOff, ListChecks, ListTree, MessageSquare,
  Paperclip, History, CornerUpLeft,
} from 'lucide-react';
import api from '@/lib/axios';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/auth.store';
import {
  useActualizarTarea, useAgregarRelacion, useEliminarTarea, useMoverTarea, usePermisosTareas, useQuitarRelacion,
  useTablero, useTarea,
} from '@/hooks/useTareas';
import type { ActualizarTareaInput, TareaDetalle } from '@/types/tareas';
import { RelacionesPicker } from '@/components/tareas/RelacionesPicker';
import { ICONO_RELACION, etiquetaDeRelacion } from '@/components/tareas/TareaPiezas';
import { RelacionResumen } from './RelacionResumen';
import { DatosTarea } from './DatosTarea';
import { SubtareasPanel } from './detalle/SubtareasPanel';
import { ChecklistsPanel } from './detalle/ChecklistsPanel';
import { ComentariosPanel } from './detalle/ComentariosPanel';
import { AdjuntosPanel } from './detalle/AdjuntosPanel';
import { ActividadPanel } from './detalle/ActividadPanel';

const TAB_GENERAL = 'general';
const TAB_COMENTARIOS = 'comentarios';
const TAB_ACTIVIDAD = 'actividad';
// Las pestañas de relaciones usan el id de la relación; estas son las fijas.
const PESTANAS_FIJAS = new Set([TAB_GENERAL, TAB_COMENTARIOS, TAB_ACTIVIDAD]);

const ocultarScrollbar = '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden';
const contenidoTab = 'flex-1 lg:overflow-y-auto p-5 outline-none';

const triggerTab = cn(
  'inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors',
  'border-transparent text-text-secondary hover:text-text-primary',
  'data-[state=active]:border-primary-500 data-[state=active]:text-primary-700',
);

interface Props {
  tareaId: string | null;
  onClose: () => void;
  /** Abre otra tarea en este mismo modal (subtareas y volver a la tarea padre). */
  onAbrirTarea: (id: string) => void;
}

function Contador({ n }: { n: number }) {
  return n > 0
    ? <span className="px-1.5 rounded-full bg-gray-100 text-gray-600 text-[11px] leading-5">{n}</span>
    : null;
}

/** Bloque de la pestaña General: encabezado con icono, contador y acción opcional. */
function Seccion({
  icono: Icono, titulo, contador = 0, accion, children,
}: { icono: ElementType; titulo: string; contador?: number; accion?: ReactNode; children: ReactNode }) {
  return (
    <section className="pb-6 mb-6 border-b border-gray-100 last:border-b-0 last:pb-0 last:mb-0">
      <header className="flex items-center justify-between gap-3 mb-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-text-primary">
          <Icono size={15} className="text-text-secondary" /> {titulo} <Contador n={contador} />
        </h3>
        {accion}
      </header>
      {children}
    </section>
  );
}

/**
 * Modal casi a pantalla completa. Se cierra solo con Esc o con la X (no con clic fuera) para no
 * perder lo que se está leyendo al cambiar entre pestañas de relaciones.
 */
export function TareaDetailModal({ tareaId, onClose, onAbrirTarea }: Props) {
  const { data: tarea, isLoading, isError } = useTarea(tareaId);
  // Mientras un campo esta en edicion, Esc debe cancelarlo y no cerrar todo el modal.
  const editandoRef = useRef(false);

  return (
    <Dialog.Root open={!!tareaId} onOpenChange={v => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
        <Dialog.Content
          onEscapeKeyDown={e => { if (editandoRef.current) e.preventDefault(); }}
          onPointerDownOutside={e => e.preventDefault()}
          onInteractOutside={e => e.preventDefault()}
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden focus:outline-none w-[calc(100vw-1.5rem)] h-[calc(100vh-1.5rem)] lg:w-[94vw] lg:max-w-[1500px] lg:h-[92vh]"
        >
          <Dialog.Title className="sr-only">Detalle de la tarea</Dialog.Title>
          <Dialog.Description className="sr-only">Edita la tarea y consulta la información de lo que está relacionado</Dialog.Description>

          {isLoading && <div className="flex-1 flex items-center justify-center text-gray-400"><Loader2 className="animate-spin" /></div>}
          {isError && (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <p className="text-sm text-text-secondary">No se encontró la tarea o ya no tienes acceso a ella.</p>
              <button onClick={onClose} className="mt-3 text-sm text-primary-600 hover:underline">Cerrar</button>
            </div>
          )}
          {tarea && <Contenido key={tarea.id} tarea={tarea} onClose={onClose} onAbrirTarea={onAbrirTarea} editandoRef={editandoRef} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Contenido({ tarea, onClose, onAbrirTarea, editandoRef }: {
  tarea: TareaDetalle; onClose: () => void; onAbrirTarea: (id: string) => void; editandoRef: MutableRefObject<boolean>;
}) {
  const usuario = useAuthStore(s => s.usuario);
  const { data: padre } = useTarea(tarea.parentId);
  const { esAdmin } = usePermisosTareas();
  const actualizar = useActualizarTarea();
  const mover = useMoverTarea(tarea.tableroId);
  const agregarRelacion = useAgregarRelacion();
  const quitarRelacion = useQuitarRelacion();
  const eliminar = useEliminarTarea();
  const { data: tablero } = useTablero(tarea.tableroId);

  const { data: consultorios = [] } = useQuery<any[]>({
    queryKey: ['consultorios'],
    queryFn: () => api.get('/api/clinica/consultorios').then(r => r.data),
    staleTime: 5 * 60_000,
  });

  const [titulo, setTitulo] = useState(tarea.titulo);
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);
  const [pestana, setPestana] = useState(TAB_GENERAL);
  const listaPestanas = useRef<HTMLDivElement>(null);

  // Re-sincroniza el titulo cuando el servidor devuelve datos mas nuevos, salvo que se este escribiendo.
  useEffect(() => {
    if (document.activeElement?.getAttribute('data-campo') !== 'titulo') setTitulo(tarea.titulo);
  }, [tarea.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Una relación oculta (paciente con ARCO) no tiene pestaña: solo aparece en General.
  const relacionesConPestana = useMemo(() => tarea.relaciones.filter(r => !r.oculto && r.entidadId), [tarea.relaciones]);

  // Si la pestaña activa desaparece (relación quitada) se vuelve a General; si aparece una nueva se abre.
  const idsPrevios = useRef(new Set(relacionesConPestana.map(r => r.id)));
  useEffect(() => {
    const idsActuales = new Set(relacionesConPestana.map(r => r.id));
    const nueva = relacionesConPestana.find(r => !idsPrevios.current.has(r.id));
    idsPrevios.current = idsActuales;
    if (nueva) setPestana(nueva.id);
    else if (!PESTANAS_FIJAS.has(pestana) && !idsActuales.has(pestana)) setPestana(TAB_GENERAL);
  }, [relacionesConPestana]); // eslint-disable-line react-hooks/exhaustive-deps

  // Con la tira de pestañas sin scrollbar, la pestaña activa debe quedar siempre visible.
  useEffect(() => {
    listaPestanas.current?.querySelector('[data-state="active"]')?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [pestana]);

  const guardar = (cambios: ActualizarTareaInput) => actualizar.mutate({ id: tarea.id, ...cambios });

  const puedeEliminar = esAdmin || tarea.creadoPorId === usuario?.id;
  const columnas = tablero?.columnas ?? [];
  const completada = !!tarea.completadaAt;

  return (
    <>
      <header className="flex items-start gap-3 px-5 py-3 border-b border-gray-100 flex-shrink-0">
        <div className="flex-1 min-w-0">
          <input
            value={titulo}
            onChange={e => setTitulo(e.target.value)}
            onBlur={() => {
              const limpio = titulo.trim();
              if (!limpio) setTitulo(tarea.titulo);
              else if (limpio !== tarea.titulo) guardar({ titulo: limpio });
            }}
            maxLength={255}
            className="w-full text-xl font-semibold text-text-primary bg-transparent rounded-md px-1 -mx-1 focus:outline-none focus:ring-2 focus:ring-primary-300"
            aria-label="Título"
            data-campo="titulo"
          />
          <p className="text-xs text-text-secondary mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {tarea.parentId && (
              <button onClick={() => onAbrirTarea(tarea.parentId as string)} className="inline-flex items-center gap-1 text-primary-600 hover:underline max-w-[24rem]">
                <CornerUpLeft size={12} className="flex-shrink-0" />
                <span className="truncate">Subtarea de «{padre?.titulo ?? '…'}»</span>
              </button>
            )}
            <span>{tarea.tablero?.nombre}</span>
            {completada && <span className="inline-flex items-center gap-0.5 text-emerald-600"><CheckCircle2 size={12} /> Completada</span>}
          </p>
        </div>
        <Dialog.Close className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100" aria-label="Cerrar (Esc)">
          <X size={20} />
        </Dialog.Close>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto lg:overflow-hidden grid grid-cols-1 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
        {/* ── Datos de la tarea ── */}
        <section className="lg:overflow-y-auto p-5 lg:border-r border-gray-100">
          <DatosTarea
            tarea={tarea}
            columnas={columnas}
            consultorios={consultorios}
            alGuardar={guardar}
            alMover={columnaId => mover.mutate({ id: tarea.id, columnaId })}
            editandoRef={editandoRef}
          />
        </section>

        {/* ── Pestañas: general y una por cada relación ── */}
        <Tabs.Root value={pestana} onValueChange={setPestana} className="flex flex-col min-h-[24rem] lg:min-h-0 lg:overflow-hidden">
          {/* Sin barras de scroll visibles: si hay muchas relaciones la tira se desplaza con rueda/gesto y la pestaña activa se mantiene a la vista. */}
          <Tabs.List ref={listaPestanas} className={cn('flex gap-1 px-4 border-b border-gray-100 overflow-x-auto overflow-y-hidden flex-shrink-0', ocultarScrollbar)} aria-label="Información de la tarea">
            <Tabs.Trigger value={TAB_GENERAL} className={triggerTab}>
              <ClipboardList size={14} /> General
            </Tabs.Trigger>
            <Tabs.Trigger value={TAB_COMENTARIOS} className={triggerTab}>
              <MessageSquare size={14} /> Comentarios <Contador n={tarea.comentarios} />
            </Tabs.Trigger>
            <Tabs.Trigger value={TAB_ACTIVIDAD} className={triggerTab}>
              <History size={14} /> Actividad
            </Tabs.Trigger>
            {relacionesConPestana.length > 0 && <span className="self-center mx-1 h-5 w-px bg-gray-200 flex-shrink-0" aria-hidden />}
            {relacionesConPestana.map(r => {
              const Icono = ICONO_RELACION[r.tipo] ?? Link2;
              return (
                <Tabs.Trigger key={r.id} value={r.id} className={triggerTab} title={etiquetaDeRelacion(r)}>
                  <Icono size={14} className="flex-shrink-0" />
                  <span className="max-w-[12rem] truncate">{etiquetaDeRelacion(r)}</span>
                </Tabs.Trigger>
              );
            })}
          </Tabs.List>

          <Tabs.Content value={TAB_GENERAL} className={contenidoTab}>
            <Seccion
              icono={Link2}
              titulo="Relacionada con"
              contador={tarea.relaciones.length}
              accion={(
                <RelacionesPicker fechaReferencia={tarea.fechaVencimiento} onAgregar={r => agregarRelacion.mutate({ id: tarea.id, entidadTipo: r.entidadTipo, entidadId: r.entidadId })}>
                  <button type="button" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-dashed border-gray-300 text-sm text-text-secondary hover:border-primary-300 hover:text-primary-600">
                    <Plus size={14} /> Agregar
                  </button>
                </RelacionesPicker>
              )}
            >
            {tarea.relaciones.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-200 py-10 px-6 text-center">
                <Link2 className="mx-auto text-gray-300" size={32} />
                <p className="mt-2 text-sm font-medium text-text-primary">Esta tarea no está ligada a nada</p>
                <p className="text-sm text-text-secondary">
                  Liga un paciente, consulta, cita o presupuesto y aparecerá aquí una pestaña con su información.
                </p>
              </div>
            ) : (
              <ul className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                {tarea.relaciones.map(r => {
                  const Icono = r.oculto ? EyeOff : ICONO_RELACION[r.tipo] ?? Link2;
                  const abrible = !r.oculto && !!r.entidadId;
                  return (
                    <li key={r.id} className={cn('flex items-center gap-2 rounded-lg border px-3 py-2.5', r.oculto ? 'border-gray-200 bg-gray-50' : 'border-border bg-white')}>
                      <button
                        type="button"
                        disabled={!abrible}
                        onClick={() => setPestana(r.id)}
                        className={cn('flex items-center gap-2 flex-1 min-w-0 text-left', abrible ? 'hover:text-primary-700' : 'cursor-default')}
                      >
                        <Icono size={16} className={cn('flex-shrink-0', r.oculto ? 'text-gray-400' : 'text-primary-600')} />
                        <span className="min-w-0">
                          <span className="block text-[11px] uppercase tracking-wide text-text-secondary">{r.tipo}</span>
                          <span className={cn('block text-sm truncate', r.oculto ? 'text-gray-400 italic' : 'text-text-primary')}>{etiquetaDeRelacion(r)}</span>
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => quitarRelacion.mutate({ id: tarea.id, relacionId: r.id })}
                        className="p-1.5 text-gray-300 hover:text-red-500"
                        aria-label="Quitar relación"
                      >
                        <X size={14} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            </Seccion>

            {/* Una subtarea no puede tener subtareas (un solo nivel). */}
            {!tarea.parentId && (
              <Seccion icono={ListTree} titulo="Subtareas" contador={tarea.subtareas.total}>
                <SubtareasPanel tarea={tarea} onAbrirTarea={onAbrirTarea} />
              </Seccion>
            )}

            <Seccion icono={ListChecks} titulo="Checklists" contador={tarea.checklist.total}>
              <ChecklistsPanel tarea={tarea} />
            </Seccion>

            <Seccion icono={Paperclip} titulo="Adjuntos" contador={tarea.adjuntos}>
              <AdjuntosPanel tarea={tarea} />
            </Seccion>
          </Tabs.Content>

          <Tabs.Content value={TAB_COMENTARIOS} className={contenidoTab}><ComentariosPanel tarea={tarea} /></Tabs.Content>
          <Tabs.Content value={TAB_ACTIVIDAD} className={contenidoTab}><ActividadPanel tarea={tarea} /></Tabs.Content>

          {relacionesConPestana.map(r => (
            <Tabs.Content key={r.id} value={r.id} className="flex-1 lg:overflow-y-auto p-5 outline-none">
              <RelacionResumen relacion={r} />
            </Tabs.Content>
          ))}
        </Tabs.Root>
      </div>

      <footer className="border-t border-gray-100 px-5 py-3 flex items-center justify-between gap-3 flex-shrink-0">
        <p className="text-[11px] text-text-secondary">
          Creada {new Date(tarea.createdAt).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
        </p>
        {puedeEliminar && (confirmandoBorrado ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-text-primary">¿Eliminar esta tarea?</span>
            <button onClick={() => setConfirmandoBorrado(false)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">
              Cancelar
            </button>
            <button
              onClick={() => eliminar.mutate(tarea, {
                // Al borrar una subtarea se vuelve a su tarea padre en lugar de cerrar todo.
                onSuccess: () => (tarea.parentId ? onAbrirTarea(tarea.parentId) : onClose()),
              })}
              disabled={eliminar.isPending}
              className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 disabled:opacity-50"
            >
              Eliminar
            </button>
          </div>
        ) : (
          <button onClick={() => setConfirmandoBorrado(true)} className="inline-flex items-center gap-1.5 text-sm text-red-500 hover:text-red-600">
            <Trash2 size={14} /> Eliminar
          </button>
        ))}
      </footer>
    </>
  );
}
